import { useState, useCallback } from 'react'
import { Issue, User, Label, Status, Priority } from './types'

// The signed-in user, used to power the "My Issues" view.
export const CURRENT_USER_ID = 'u1'

export const USERS: User[] = [
  {
    id: 'u1',
    name: 'Alice Smith',
    avatar:
      'https://api.dicebear.com/7.x/notionists/svg?seed=Alice&backgroundColor=e2e8f0',
  },
  {
    id: 'u2',
    name: 'Bob Jones',
    avatar:
      'https://api.dicebear.com/7.x/notionists/svg?seed=Bob&backgroundColor=e2e8f0',
  },
  {
    id: 'u3',
    name: 'Charlie Brown',
    avatar:
      'https://api.dicebear.com/7.x/notionists/svg?seed=Charlie&backgroundColor=e2e8f0',
  },
]

export const LABELS: Label[] = [
  { id: 'l1', name: 'Bug', color: '#ef4444' },
  { id: 'l2', name: 'Feature', color: '#3b82f6' },
  { id: 'l3', name: 'Improvement', color: '#10b981' },
  { id: 'l4', name: 'Design', color: '#8b5cf6' },
]

export const STATUSES: { id: Status; label: string }[] = [
  { id: 'backlog', label: 'Backlog' },
  { id: 'todo', label: 'Todo' },
  { id: 'in-progress', label: 'In Progress' },
  { id: 'in-review', label: 'In Review' },
  { id: 'done', label: 'Done' },
  { id: 'canceled', label: 'Canceled' },
]

export const PRIORITIES: { id: Priority; label: string }[] = [
  { id: 'no-priority', label: 'No priority' },
  { id: 'urgent', label: 'Urgent' },
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
]

const INITIAL_ISSUES: Issue[] = [
  {
    id: 'i1',
    identifier: 'ENG-1',
    title: 'Setup project repository',
    description: 'Initialize the monorepo with React and Tailwind.',
    status: 'done',
    priority: 'high',
    assigneeId: 'u1',
    labelIds: ['l2'],
    createdAt: new Date(Date.now() - 86400000 * 5).toISOString(),
    updatedAt: new Date(Date.now() - 86400000 * 4).toISOString(),
  },
  {
    id: 'i2',
    identifier: 'ENG-2',
    title: 'Design system foundation',
    description:
      'Create base components: Button, Input, Modal, and typography scale.',
    status: 'in-review',
    priority: 'urgent',
    assigneeId: 'u2',
    labelIds: ['l4'],
    createdAt: new Date(Date.now() - 86400000 * 4).toISOString(),
    updatedAt: new Date(Date.now() - 86400000 * 1).toISOString(),
  },
  {
    id: 'i3',
    identifier: 'ENG-3',
    title: 'Implement Kanban board drag and drop',
    description: 'Allow users to drag issues between columns to update status.',
    status: 'in-progress',
    priority: 'high',
    assigneeId: 'u3',
    labelIds: ['l2', 'l3'],
    createdAt: new Date(Date.now() - 86400000 * 3).toISOString(),
    updatedAt: new Date(Date.now() - 86400000 * 0.5).toISOString(),
  },
  {
    id: 'i4',
    identifier: 'ENG-4',
    title: 'Fix sidebar navigation active state',
    description: 'Active state is not persisting across page reloads.',
    status: 'todo',
    priority: 'medium',
    assigneeId: 'u1',
    labelIds: ['l1'],
    createdAt: new Date(Date.now() - 86400000 * 2).toISOString(),
    updatedAt: new Date(Date.now() - 86400000 * 2).toISOString(),
  },
  {
    id: 'i5',
    identifier: 'ENG-5',
    title: 'Add dark mode support',
    description: 'Implement dark mode color palette and theme toggle.',
    status: 'backlog',
    priority: 'low',
    assigneeId: undefined,
    labelIds: ['l2', 'l4'],
    createdAt: new Date(Date.now() - 86400000 * 1).toISOString(),
    updatedAt: new Date(Date.now() - 86400000 * 1).toISOString(),
  },
  {
    id: 'i6',
    identifier: 'ENG-6',
    title: 'Optimize list view rendering',
    description:
      'List view is slow with >1000 items. Implement virtualization.',
    status: 'todo',
    priority: 'high',
    assigneeId: 'u2',
    labelIds: ['l3'],
    createdAt: new Date(Date.now() - 43200000).toISOString(),
    updatedAt: new Date(Date.now() - 43200000).toISOString(),
  },
  {
    id: 'i7',
    identifier: 'ENG-7',
    title: 'Update dependencies',
    description: 'Bump React to 18.3 and Tailwind to v3.4.',
    status: 'canceled',
    priority: 'no-priority',
    assigneeId: 'u3',
    labelIds: [],
    createdAt: new Date(Date.now() - 86400000 * 10).toISOString(),
    updatedAt: new Date(Date.now() - 86400000 * 9).toISOString(),
  },
]

export function useIssueStore() {
  const [issues, setIssues] = useState<Issue[]>(INITIAL_ISSUES)

  const addIssue = useCallback(
    (issue: Omit<Issue, 'id' | 'identifier' | 'createdAt' | 'updatedAt'>) => {
      setIssues((prev) => {
        const nextNum = prev.length + 1
        const newId = `i${nextNum}`
        const newIdentifier = `ENG-${nextNum}`
        const now = new Date().toISOString()
        return [
          ...prev,
          {
            ...issue,
            id: newId,
            identifier: newIdentifier,
            createdAt: now,
            updatedAt: now,
          },
        ]
      })
    },
    [],
  )

  const updateIssue = useCallback((id: string, updates: Partial<Issue>) => {
    setIssues((prev) =>
      prev.map((issue) =>
        issue.id === id
          ? { ...issue, ...updates, updatedAt: new Date().toISOString() }
          : issue,
      ),
    )
  }, [])

  return {
    issues,
    addIssue,
    updateIssue,
  }
}
